import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import './rewardsPointEvents.css';

const EMPTY_FORM = {
  name: '',
  multiplier: '2',
  startsAt: '',
  endsAt: '',
  active: true,
};

function pad(value) {
  return String(value).padStart(2, '0');
}

function toDatetimeLocalValue(ts) {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatRange(event) {
  const start = event.startsAt?.toDate?.();
  const end = event.endsAt?.toDate?.();
  const fmt = (date) =>
    date
      ? date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : '—';
  return `${fmt(start)} – ${fmt(end)}`;
}

function eventStatus(event, now) {
  const start = event.startsAt?.toMillis?.() ?? 0;
  const end = event.endsAt?.toMillis?.() ?? 0;
  if (event.active === false) return { id: 'off', label: 'Off' };
  if (now < start) return { id: 'upcoming', label: 'Upcoming' };
  if (now > end) return { id: 'ended', label: 'Ended' };
  return { id: 'live', label: 'Live' };
}

function formatMultiplier(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n}×`;
}

const RewardsPointEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const q = query(collection(db, 'RewardsPointEvents'), orderBy('startsAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEvents(snap.docs.map((eventDoc) => ({ id: eventDoc.id, ...eventDoc.data() })));
        setLoadError('');
        setLoading(false);
      },
      (err) => {
        console.error('Point events listener failed:', err);
        setLoadError(err?.message || 'Could not load events.');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError('');
  };

  const onEdit = (event) => {
    setEditingId(event.id);
    setFormError('');
    setForm({
      name: event.name || '',
      multiplier: String(event.multiplier ?? ''),
      startsAt: toDatetimeLocalValue(event.startsAt),
      endsAt: toDatetimeLocalValue(event.endsAt),
      active: event.active !== false,
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const multiplier = Number(form.multiplier);
    const start = form.startsAt ? new Date(form.startsAt) : null;
    const end = form.endsAt ? new Date(form.endsAt) : null;

    if (!name) {
      setFormError('Enter a name for this event.');
      return;
    }
    if (!Number.isFinite(multiplier) || multiplier <= 1 || multiplier > 10) {
      setFormError('Multiplier must be greater than 1 and at most 10.');
      return;
    }
    if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
      setFormError('Choose a start and an end.');
      return;
    }
    if (end.getTime() <= start.getTime()) {
      setFormError('The end must be after the start.');
      return;
    }

    setSaving(true);
    setFormError('');
    const payload = {
      name,
      multiplier,
      startsAt: Timestamp.fromDate(start),
      endsAt: Timestamp.fromDate(end),
      active: form.active,
      updatedAt: serverTimestamp(),
    };
    try {
      if (editingId) {
        await updateDoc(doc(db, 'RewardsPointEvents', editingId), payload);
      } else {
        await addDoc(collection(db, 'RewardsPointEvents'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
    } catch (err) {
      console.error('Save point event failed:', err);
      setFormError(err?.message || 'Could not save this event.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (event) => {
    const ok = window.confirm(`Delete “${event.name || 'this event'}”? Orders already awarded keep their points.`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'RewardsPointEvents', event.id));
      if (editingId === event.id) resetForm();
    } catch (err) {
      console.error('Delete point event failed:', err);
      setLoadError(err?.message || 'Could not delete this event.');
    }
  };

  const onToggle = async (event) => {
    try {
      await updateDoc(doc(db, 'RewardsPointEvents', event.id), {
        active: event.active === false,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Toggle point event failed:', err);
      setLoadError(err?.message || 'Could not update this event.');
    }
  };

  return (
    <div className="point-events-page">
      <div className="point-events-header">
        <h1>Point events</h1>
        <p className="point-events-subtitle">
          During an event, paid orders earn their usual tier points multiplied by the event rate.
          If two events overlap, the higher multiplier is used.
        </p>
      </div>

      <form className="point-events-form" onSubmit={onSave}>
        <h2>{editingId ? 'Edit event' : 'New event'}</h2>
        <div className="point-events-fields">
          <label>
            Name
            <input
              type="text"
              value={form.name}
              maxLength={80}
              placeholder="Holiday double points"
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label>
            Multiplier
            <input
              type="number"
              min="1.1"
              max="10"
              step="0.1"
              value={form.multiplier}
              onChange={(e) => setForm((prev) => ({ ...prev, multiplier: e.target.value }))}
            />
          </label>
          <label>
            Starts
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
            />
          </label>
          <label>
            Ends
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
            />
          </label>
        </div>
        <label className="point-events-active">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
          />
          Active
        </label>
        {formError && <p className="point-events-error">{formError}</p>}
        <div className="point-events-actions">
          <button type="submit" className="point-events-save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create event'}
          </button>
          {editingId && (
            <button type="button" className="point-events-cancel" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {loadError && <p className="point-events-error">{loadError}</p>}
      {loading ? (
        <p className="point-events-empty">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="point-events-empty">No events yet. Create one to multiply points for a set time.</p>
      ) : (
        <div className="point-events-list">
          {events.map((event) => {
            const status = eventStatus(event, now);
            return (
              <article key={event.id} className="point-events-card">
                <div className="point-events-card-top">
                  <h3>{event.name || 'Untitled event'}</h3>
                  <span className={`point-events-status is-${status.id}`}>{status.label}</span>
                </div>
                <p className="point-events-mult">{formatMultiplier(event.multiplier)} points</p>
                <p className="point-events-when">{formatRange(event)}</p>
                <div className="point-events-card-actions">
                  <button type="button" onClick={() => onEdit(event)}>Edit</button>
                  <button type="button" onClick={() => onToggle(event)}>
                    {event.active === false ? 'Turn on' : 'Turn off'}
                  </button>
                  <button type="button" className="is-danger" onClick={() => onDelete(event)}>
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RewardsPointEvents;
